import { userAvatarBucketName } from '@/config/env';
import type {
  ConversationSearchItem,
  ConversationSearchResponse
} from '@/features/conversation/models/conversation-dto';
import { GradientAvatar } from '@/shared/components';
import { formatDateCompact } from '@/shared/utilities';
import { cn } from '@/shared/utilities/cn';
import { Box, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface DrawerSearchProps {
  item: ConversationSearchItem;
  isSelected: boolean;
  onClick: (item: ConversationSearchItem) => void;
}

function DrawerSearch({ item, isSelected, onClick }: DrawerSearchProps) {
  const theme = useTheme();

  const handleClick = () => {
    onClick(item);
  };

  const getFormattedDate = () => {
    if (!item.createdAt) return '';
    return formatDateCompact(item.createdAt);
  };

  // For search results, we don't have full conversation type info
  // Default to group style for now, can be extended later
  const avatarVariant = 'rounded';
  const bucketName = userAvatarBucketName;

  return (
    <Box
      onClick={handleClick}
      className={cn(
        'mex:flex mex:cursor-pointer mex:items-center mex:rounded-lg mex:transition-all mex:duration-200 mex:hover:bg-gray-50',
        'mex:hover:dark:bg-primary-900/20',
        isSelected && 'mex:bg-primary-50 mex:dark:bg-primary-900/20',
        'mex:p-3'
      )}
      sx={{
        gap: 3,
        '&:hover': {
          backgroundColor: theme.vars.palette.action.hover
        },
        ...(isSelected && {
          backgroundColor: `${theme.vars.palette.primary.main}15`,
          borderLeft: `3px solid ${theme.vars.palette.primary.main}`
        })
      }}
    >
      <Box position='relative'>
        <GradientAvatar
          peerId={item.conversationId}
          variant={avatarVariant}
          name={item.conversationName}
          src={item.conversationAvatarRef || ''}
          bucketName={bucketName}
        />
      </Box>

      <Stack direction='column' spacing={0.5} flex={1} minWidth={0}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography
            variant='subtitle2'
            noWrap
            sx={{
              fontWeight: 500,
              color: isSelected ? theme.vars.palette.primary.main : 'inherit'
            }}
          >
            {item.conversationName}
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0, ml: 1 }}>
            {getFormattedDate()}
          </Typography>
        </Box>

        {/* <Typography
          variant='body2'
          color='text.secondary'
          noWrap
          sx={{
            fontWeight: 400
          }}
        ></Typography> */}
      </Stack>
    </Box>
  );
}

interface ConversationSearchResultsProps {
  query: string;
  conversationId?: string;
  onSelect?: (conv: ConversationSearchItem) => void;
  searchData: { pages: ConversationSearchResponse[] } | undefined;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetching: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function ConversationSearchResults({
  conversationId,
  onSelect,
  searchData,
  fetchNextPage,
  hasNextPage,
  isFetching,
  isLoading,
  error
}: ConversationSearchResultsProps) {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement>(null);

  const searchItems = useMemo((): ConversationSearchItem[] => {
    if (!searchData?.pages) return [];
    return searchData.pages.flatMap((page: ConversationSearchResponse) => page.items) || [];
  }, [searchData?.pages]);

  const virtualizer = useVirtualizer({
    count: searchItems.length + (hasNextPage ? 1 : 0),
    estimateSize: () => 72,
    getScrollElement: () => parentRef.current,
    overscan: 8,
    gap: 2,
    getItemKey: (index) => searchItems[index]?.id || index
  });

  const handleScroll = useCallback(() => {
    const el = parentRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      virtualizer.getVirtualItems().length === searchItems.length &&
      hasNextPage &&
      !isFetching
    ) {
      fetchNextPage();
    }
  }, [virtualizer, searchItems.length, hasNextPage, isFetching, fetchNextPage]);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    let ticking = false;
    const throttledScroll = () => {
      if (ticking) return;
      requestAnimationFrame(() => {
        ticking = false;
        handleScroll();
      });
      ticking = true;
    };

    el.addEventListener('scroll', throttledScroll, { passive: true });
    return () => el.removeEventListener('scroll', throttledScroll);
  }, [handleScroll]);

  if (error) {
    return (
      <Box className='mex:flex mex:items-center mex:justify-center mex:p-8'>
        <Typography color='error' variant='body2'>
          {t('conversations.search.error')}
        </Typography>
      </Box>
    );
  }

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  if (isLoading && searchItems.length === 0) {
    return (
      <Box className='mex:flex mex:h-full mex:items-center mex:justify-center mex:p-8'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className='mex:h-full' aria-label={t('conversations.search.results')}>
      <Box
        ref={parentRef}
        className='mex:h-full mex:overflow-auto mex:px-2'
        style={{ scrollbarWidth: 'thin' }}
        aria-busy={isFetching}
      >
        <Box
          style={{
            height: `${totalSize}px`,
            width: '100%',
            position: 'relative'
          }}
        >
          {items.map((item) => {
            if (item.index >= searchItems.length) {
              return (
                <Box
                  key='loading-end'
                  ref={virtualizer.measureElement}
                  data-index={item.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: 80,
                    transform: `translateY(${item.start}px)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  aria-label={t('conversations.search.loadingMore')}
                >
                  <CircularProgress size={20} />
                </Box>
              );
            }

            const searchItem = searchItems[item.index];

            return (
              <Box
                key={searchItem.id}
                ref={virtualizer.measureElement}
                data-index={item.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  width: '100%',
                  transform: `translateY(${item.start}px)`
                }}
                className='mex:mb-1'
              >
                <DrawerSearch item={searchItem} isSelected={conversationId === searchItem.id} onClick={onSelect!} />
              </Box>
            );
          })}
        </Box>
      </Box>
      {searchItems.length === 0 && !isLoading && (
        <Box
          className='mex:absolute mex:inset-0 mex:flex mex:items-center mex:justify-center mex:p-8'
          aria-live='polite'
        >
          <Typography color='text.secondary' variant='body2'>
            {t('conversations.search.empty')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
